import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card.js';
import { Money } from '@/components/money/Money.js';
import { SPIN_QUERY_KEYS, fetchSpinHistory } from '@/features/spin/api.js';
import { prizeLabel } from '@/features/spin/prize-label.js';
import { formatDateTime } from '@/lib/datetime/format.js';

/** The member's recent Lucky Spin draws, newest first, as `/spin/history` returns them. */
export function SpinHistoryList({ currency }: { currency: string }) {
  const { t, i18n } = useTranslation('spin');
  const history = useQuery({
    queryKey: SPIN_QUERY_KEYS.history,
    queryFn: fetchSpinHistory,
    staleTime: 30_000,
  });

  if (history.isPending) {
    return (
      <div className="flex flex-col gap-2">
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="h-16 rounded-xl bg-[var(--bg-elevated)]/50 animate-pulse" />
        ))}
      </div>
    );
  }

  const items = history.data?.items ?? [];
  if (items.length === 0) {
    return (
      <div className="rounded-xl bg-[var(--bg-raised)] py-10 text-center text-[var(--text-muted)] text-[13px]">
        {t('history.empty')}
      </div>
    );
  }

  return (
    <Card className="overflow-hidden">
      <ul>
        {items.map((spin, i) => (
          <li
            key={spin.id}
            className={[
              'flex items-center justify-between gap-3 px-4 py-3',
              i > 0 ? 'border-t border-[var(--border-subtle)]' : '',
            ].join(' ')}
          >
            <div className="min-w-0">
              <div className="text-[13px] font-semibold text-[var(--text-primary)] truncate">
                {prizeLabel(spin.prizeValue, t)}
              </div>
              <div className="text-[11px] text-[var(--text-muted)] mt-0.5">
                {formatDateTime(spin.createdAt, i18n.language)}
              </div>
            </div>
            {spin.cashAwarded > 0 ? (
              <span className="shrink-0 text-[13px] font-bold tabular-nums text-[var(--success)]">
                + <Money minor={spin.cashAwarded} currency={currency} />
              </span>
            ) : (
              <span className="shrink-0 text-[12px] text-[var(--text-muted)]">{t('result.nothing')}</span>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}
